package es.cragma.fichajefacil.ui.screens.worker

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.getCurrentTimeMillis
import es.cragma.fichajefacil.getCurrentGpsLocation
import es.cragma.fichajefacil.DeviceLocation
import es.cragma.fichajefacil.model.*
import es.cragma.fichajefacil.ui.components.*
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*
import kotlinx.coroutines.delay

fun formatSimpleTime(millis: Long): String {
    val totalSecs = millis / 1000
    val hours = ((totalSecs / 3600) + 2) % 24
    val mins = (totalSecs % 3600) / 60
    return "${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} hs"
}

enum class AppWorkerTab {
    INICIO,
    VACACIONES,
    ACTION_FAB,
    CALENDARIO,
    PERFIL
}

data class DynamicDayInfo(
    val dayName: String,       // "LUN", "MAR", etc.
    val dayNumber: Int,        // 1..31
    val monthNumber: Int,      // 1..12
    val yearNumber: Int,       // 2026
    val dateStr: String,       // "24/03/2026"
    val isToday: Boolean
)

@Composable
fun WorkerHomeScreen(
    user: User,
    repository: AppRepository,
    currentLanguage: AppLanguage = AppLanguage.SPANISH,
    onSwitchToManagementMode: (() -> Unit)? = null,
    onLogout: () -> Unit,
    onSwitchCompany: (() -> Unit)? = null,
    onRequestGpsLocation: ((onResult: (DeviceLocation?) -> Unit) -> Unit)? = null
) {
    val s = remember(currentLanguage) { LocalizedStrings.get(currentLanguage) }

    var selectedTab by remember { mutableStateOf(AppWorkerTab.INICIO) }

    var activeClockIn by remember { mutableStateOf(repository.getActiveClockIn(user.id, user.email)) }
    var historyRecords by remember { mutableStateOf(repository.getClockInHistory(user, user.id)) }
    var isLocationSharingEnabled by remember { mutableStateOf(user.allowUnverifiedLocationClockIn) }

    // Dialog & Banner states
    var showStartShiftConfirmDialog by remember { mutableStateOf(false) }
    var showStopShiftOptionDialog by remember { mutableStateOf(false) }
    var confirmationBannerMessage by remember { mutableStateOf<String?>(null) }

    // Live clock ticker
    var currentTimeMillis by remember { mutableStateOf(getCurrentTimeMillis()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(1000)
            currentTimeMillis = getCurrentTimeMillis()
        }
    }

    // Dynamic date calculations
    val todayDateStr = remember(currentTimeMillis) {
        formatDateTimeString(currentTimeMillis).take(10) // "24/03/2026"
    }

    val currentMonthStr = remember(currentTimeMillis) {
        val full = formatDateTimeString(currentTimeMillis)
        if (full.length >= 10) full.substring(3, 10) else "03/2026" // "03/2026"
    }

    // Calendar state initialized to current month/year
    var calYear by remember {
        mutableStateOf(
            formatDateTimeString(currentTimeMillis).take(10).split("/").getOrNull(2)?.toIntOrNull() ?: 2026
        )
    }
    var calMonth by remember {
        mutableStateOf(
            formatDateTimeString(currentTimeMillis).take(10).split("/").getOrNull(1)?.toIntOrNull() ?: 3
        )
    }
    var selectedCalDay by remember {
        mutableStateOf<Int?>(
            formatDateTimeString(currentTimeMillis).take(10).split("/").getOrNull(0)?.toIntOrNull() ?: 24
        )
    }
    var weekOffset by remember { mutableStateOf(0) } // Offset in weeks for weekly strip

    val company = user.companyId?.let { repository.getCompany(it) }
    val scrollState = rememberScrollState()

    fun refreshState() {
        val currentActive = repository.getActiveClockIn(user.id, user.email)
        activeClockIn = currentActive?.copy(breaks = currentActive.breaks.toMutableList())
        historyRecords = repository.getClockInHistory(user, user.id).toList()
    }

    LaunchedEffect(user.id, user.email) {
        refreshState()
    }

    // Dynamic Week Days Calculation
    val weekDays = remember(currentTimeMillis, weekOffset) {
        calculateWeekDays(currentTimeMillis, weekOffset)
    }

    // Calculate Dynamic Summaries
    val liveActiveShiftMillis = remember(activeClockIn, currentTimeMillis) {
        if (activeClockIn?.currentStatus == ClockInStatus.WORKING) {
            (currentTimeMillis - activeClockIn!!.startTimeMillis - activeClockIn!!.totalBreakMillis).coerceAtLeast(0L)
        } else 0L
    }

    val workedTodayMillis = remember(historyRecords, todayDateStr, liveActiveShiftMillis) {
        val pastToday = historyRecords
            .filter { it.startDateString.startsWith(todayDateStr) }
            .sumOf { it.netWorkedMillis }
        pastToday + liveActiveShiftMillis
    }

    val workedWeekMillis = remember(historyRecords, weekDays, liveActiveShiftMillis) {
        val weekDates = weekDays.map { it.dateStr }
        val pastWeek = historyRecords
            .filter { rec -> weekDates.any { rec.startDateString.startsWith(it) } }
            .sumOf { it.netWorkedMillis }
        pastWeek + liveActiveShiftMillis
    }

    val workedMonthMillis = remember(historyRecords, currentMonthStr, liveActiveShiftMillis) {
        val pastMonth = historyRecords
            .filter { it.startDateString.contains(currentMonthStr) }
            .sumOf { it.netWorkedMillis }
        pastMonth + liveActiveShiftMillis
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF0F172A)) // Cragma Dark Navy
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(bottom = 72.dp)
        ) {
            // Top Navigation Bar
            Surface(
                color = Color(0xFF1E293B),
                shadowElevation = 4.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .windowInsetsPadding(WindowInsets.statusBars)
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text(
                                text = user.name,
                                fontWeight = FontWeight.Bold,
                                color = Color.White,
                                fontSize = 16.sp
                            )
                            Spacer(modifier = Modifier.width(6.dp))
                            RoleBadge(role = user.role, currentLanguage = currentLanguage)
                        }
                        Text(
                            text = company?.name ?: s.appTitle,
                            fontSize = 11.sp,
                            color = Color(0xFF94A3B8)
                        )
                    }

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        if (user.role.isManagement && onSwitchToManagementMode != null) {
                            Button(
                                onClick = onSwitchToManagementMode,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = Color(0xFF0284C7),
                                    contentColor = Color.White
                                ),
                                shape = RoundedCornerShape(8.dp),
                                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                                modifier = Modifier.padding(end = 6.dp)
                            ) {
                                Text("📊 Modo Admin", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                            }
                        }

                        IconButton(onClick = { selectedTab = AppWorkerTab.PERFIL }) {
                            Text("👤", fontSize = 18.sp)
                        }
                    }
                }
            }

            // MAIN CONTENT BODY
            when (selectedTab) {
                AppWorkerTab.INICIO -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(scrollState)
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        val currentStatus = activeClockIn?.currentStatus ?: ClockInStatus.NOT_STARTED

                        // Header Shift Greeting & Timer Ring Card
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(20.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B)),
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                        ) {
                            Column(
                                modifier = Modifier.padding(18.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                if (confirmationBannerMessage != null) {
                                    Surface(
                                        color = Color(0xFFDCFCE7),
                                        shape = RoundedCornerShape(10.dp),
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(bottom = 12.dp)
                                    ) {
                                        Text(
                                            text = confirmationBannerMessage!!,
                                            color = Color(0xFF15803D),
                                            fontSize = 13.sp,
                                            fontWeight = FontWeight.Bold,
                                            modifier = Modifier.padding(10.dp),
                                            textAlign = TextAlign.Center
                                        )
                                    }
                                }

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = when (currentStatus) {
                                            ClockInStatus.NOT_STARTED -> "Hola ${user.firstName}, inicia tu jornada laboral"
                                            ClockInStatus.WORKING -> "Hola ${user.firstName}, pausa tu jornada laboral cuando quieras"
                                            ClockInStatus.ON_BREAK -> "Hola ${user.firstName}, estás en tiempo de descanso"
                                            ClockInStatus.FINISHED -> "Hola ${user.firstName}, jornada finalizada por hoy"
                                        },
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color.White,
                                        modifier = Modifier.weight(1f)
                                    )

                                    val liveMillis = when (currentStatus) {
                                        ClockInStatus.WORKING -> (currentTimeMillis - (activeClockIn?.startTimeMillis ?: currentTimeMillis) - (activeClockIn?.totalBreakMillis ?: 0L)).coerceAtLeast(0L)
                                        ClockInStatus.ON_BREAK -> (currentTimeMillis - (activeClockIn?.breaks?.lastOrNull()?.startTimeMillis ?: currentTimeMillis)).coerceAtLeast(0L)
                                        else -> 0L
                                    }

                                    Surface(
                                        color = Color(0xFF334155),
                                        shape = CircleShape,
                                        modifier = Modifier.size(54.dp)
                                    ) {
                                        Box(contentAlignment = Alignment.Center) {
                                            Text(
                                                text = if (liveMillis > 0) formatDurationMillis(liveMillis).take(5) else "00:00",
                                                fontSize = 11.sp,
                                                fontWeight = FontWeight.Bold,
                                                color = Color(0xFF38BDF8)
                                            )
                                        }
                                    }
                                }

                                Spacer(modifier = Modifier.height(16.dp))

                                when (currentStatus) {
                                    ClockInStatus.NOT_STARTED -> {
                                        Button(
                                            onClick = { showStartShiftConfirmDialog = true },
                                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                                            shape = RoundedCornerShape(24.dp),
                                            modifier = Modifier.fillMaxWidth().height(48.dp)
                                        ) {
                                            Text("▶ Iniciar Jornada", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                        }
                                    }

                                    ClockInStatus.WORKING, ClockInStatus.ON_BREAK -> {
                                        Button(
                                            onClick = { showStopShiftOptionDialog = true },
                                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                                            shape = RoundedCornerShape(24.dp),
                                            modifier = Modifier.fillMaxWidth().height(48.dp)
                                        ) {
                                            Text("Detener Jornada", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                        }
                                    }

                                    ClockInStatus.FINISHED -> {
                                        Text("Jornada laboral finalizada hoy. ¡Excelente trabajo!", fontSize = 13.sp, color = Color(0xFF94A3B8))
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(18.dp))

                        // Weekly Days Strip Navigation Bar (DYNAMIC CALCULATIONS)
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            IconButton(onClick = { weekOffset-- }) {
                                Text("◀", color = Color.White, fontSize = 14.sp)
                            }

                            val weekRangeText = if (weekDays.isNotEmpty()) {
                                "${weekDays.first().dayName} ${weekDays.first().dayNumber} - ${weekDays.last().dayName} ${weekDays.last().dayNumber}"
                            } else "Semana Actual"

                            Text(
                                text = if (weekOffset == 0) "Semana Actual ($weekRangeText)" else weekRangeText,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )

                            IconButton(onClick = { weekOffset++ }) {
                                Text("▶", color = Color.White, fontSize = 14.sp)
                            }
                        }

                        Spacer(modifier = Modifier.height(6.dp))

                        // DYNAMIC WEEKLY DAYS STRIP
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            weekDays.forEach { dayInfo ->
                                val dayWorkedMillis = historyRecords
                                    .filter { it.startDateString.startsWith(dayInfo.dateStr) }
                                    .sumOf { it.netWorkedMillis } + (if (dayInfo.isToday) liveActiveShiftMillis else 0L)

                                val dayTimeStr = if (dayWorkedMillis > 0) formatDurationMillis(dayWorkedMillis).take(5) else "00:00"

                                Surface(
                                    color = if (dayInfo.isToday) Color(0xFF1E293B) else Color(0xFF1E293B).copy(alpha = 0.5f),
                                    shape = RoundedCornerShape(12.dp),
                                    border = if (dayInfo.isToday) BorderStroke(1.5.dp, Color(0xFF0284C7)) else null,
                                    modifier = Modifier.weight(1f).padding(vertical = 2.dp)
                                ) {
                                    Column(
                                        modifier = Modifier.padding(vertical = 8.dp, horizontal = 2.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Text(dayInfo.dayName, fontSize = 9.sp, color = Color(0xFF94A3B8), fontWeight = FontWeight.Bold)
                                        Text("${dayInfo.dayNumber}", fontSize = 13.sp, color = Color.White, fontWeight = FontWeight.Bold)
                                        Spacer(modifier = Modifier.height(2.dp))
                                        Surface(
                                            color = if (dayWorkedMillis > 0) Color(0xFF0284C7).copy(alpha = 0.3f) else Color(0xFF334155),
                                            shape = RoundedCornerShape(8.dp)
                                        ) {
                                            Text(
                                                text = dayTimeStr,
                                                fontSize = 9.sp,
                                                color = if (dayWorkedMillis > 0) Color(0xFF38BDF8) else Color(0xFF94A3B8),
                                                modifier = Modifier.padding(horizontal = 4.dp, vertical = 1.dp)
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(18.dp))

                        // Resumen Semanal Section (DYNAMIC REAL DATA)
                        Text("Resumen de actividad", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White, modifier = Modifier.fillMaxWidth())
                        Spacer(modifier = Modifier.height(8.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Card(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                colors = CardDefaults.cardColors(containerColor = Color(0xFF0284C7))
                            ) {
                                Column(modifier = Modifier.padding(12.dp)) {
                                    Text(
                                        text = formatDurationMillis(workedTodayMillis),
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        color = Color.White
                                    )
                                    Text("Trabajadas hoy", fontSize = 10.sp, color = Color(0xFFE0F2FE))
                                }
                            }

                            Card(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                colors = CardDefaults.cardColors(containerColor = Color(0xFF2563EB))
                            ) {
                                Column(modifier = Modifier.padding(12.dp)) {
                                    Text(
                                        text = formatDurationMillis(workedWeekMillis),
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        color = Color.White
                                    )
                                    Text("Trabajadas esta semana", fontSize = 10.sp, color = Color(0xFFDBEAFE))
                                }
                            }

                            Card(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                colors = CardDefaults.cardColors(containerColor = Color(0xFF1E3A8A))
                            ) {
                                Column(modifier = Modifier.padding(12.dp)) {
                                    Text(
                                        text = formatDurationMillis(workedMonthMillis),
                                        fontSize = 15.sp,
                                        fontWeight = FontWeight.ExtraBold,
                                        color = Color.White
                                    )
                                    Text("Trabajadas este mes", fontSize = 10.sp, color = Color(0xFFE0E7FF))
                                }
                            }
                        }

                        Spacer(modifier = Modifier.height(18.dp))

                        // Próximos Eventos Section
                        Text("Próximos eventos en la empresa", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White, modifier = Modifier.fillMaxWidth())
                        Spacer(modifier = Modifier.height(8.dp))

                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))
                        ) {
                            Box(
                                modifier = Modifier.fillMaxWidth().padding(20.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text("No hay eventos próximos (Próximamente)", fontSize = 13.sp, color = Color(0xFF94A3B8))
                            }
                        }
                    }
                }

                AppWorkerTab.VACACIONES -> {
                    // VACACIONES TAB (Próximamente)
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(scrollState)
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text("🏖 Vacaciones y Ausencias", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color.White, modifier = Modifier.fillMaxWidth())
                        Spacer(modifier = Modifier.height(16.dp))

                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))
                        ) {
                            Column(
                                modifier = Modifier.padding(24.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Text("☀️", fontSize = 32.sp)
                                Spacer(modifier = Modifier.height(12.dp))
                                Text("Gestión de Vacaciones Solicitadas a la Empresa", fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                Spacer(modifier = Modifier.height(6.dp))
                                Text("Próximamente podrás consultar y solicitar tus ausencias y vacaciones anuales directamente desde aquí.", fontSize = 12.sp, color = Color(0xFF94A3B8), textAlign = TextAlign.Center)
                            }
                        }
                    }
                }

                AppWorkerTab.CALENDARIO -> {
                    // CALENDARIO TAB (DYNAMIC DATES)
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(scrollState)
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(20.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))
                        ) {
                            Column(modifier = Modifier.padding(16.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    IconButton(onClick = {
                                        if (calMonth == 1) { calMonth = 12; calYear-- } else calMonth--
                                    }) { Text("◀", color = Color.White) }

                                    val monthNames = listOf("Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre")
                                    Text("${monthNames.getOrNull(calMonth - 1)} $calYear", fontSize = 16.sp, fontWeight = FontWeight.Bold, color = Color.White)

                                    IconButton(onClick = {
                                        if (calMonth == 12) { calMonth = 1; calYear++ } else calMonth++
                                    }) { Text("▶", color = Color.White) }
                                }

                                Spacer(modifier = Modifier.height(12.dp))

                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                    listOf("L", "M", "X", "J", "V", "S", "D").forEach { d ->
                                        Text(d, fontSize = 11.sp, fontWeight = FontWeight.Bold, color = Color(0xFF94A3B8), modifier = Modifier.weight(1f), textAlign = TextAlign.Center)
                                    }
                                }

                                Spacer(modifier = Modifier.height(8.dp))

                                val daysInMonth = getDaysInMonth(calYear, calMonth)
                                val startOffset = getDayOfWeekForFirstDayOfMonth(calYear, calMonth) - 1
                                val totalCells = startOffset + daysInMonth
                                val totalWeeks = (totalCells + 6) / 7

                                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                    for (w in 0 until totalWeeks) {
                                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                            for (dow in 0..6) {
                                                val cellIdx = w * 7 + dow
                                                val dayNum = cellIdx - startOffset + 1

                                                if (cellIdx in startOffset until (startOffset + daysInMonth)) {
                                                    val isSelected = selectedCalDay == dayNum
                                                    val dayMatch = "${dayNum.toString().padStart(2, '0')}/${calMonth.toString().padStart(2, '0')}/$calYear"
                                                    val dayRecs = historyRecords.filter { it.startDateString.startsWith(dayMatch) }
                                                    val hasHistory = dayRecs.isNotEmpty()
                                                    val dayWorked = dayRecs.sumOf { it.netWorkedMillis }

                                                    Surface(
                                                        onClick = { selectedCalDay = dayNum },
                                                        color = if (isSelected) Color(0xFF0284C7) else if (hasHistory) Color(0xFF16A34A).copy(alpha = 0.3f) else Color(0xFF334155),
                                                        shape = RoundedCornerShape(10.dp),
                                                        border = if (isSelected) BorderStroke(2.dp, Color(0xFF38BDF8)) else null,
                                                        modifier = Modifier.weight(1f).height(42.dp)
                                                    ) {
                                                        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                                                            Text("$dayNum", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                                            if (hasHistory) {
                                                                Text(if (dayWorked > 0) formatDurationMillis(dayWorked).take(4) else "🟢", fontSize = 8.sp, color = Color(0xFF38BDF8))
                                                            }
                                                        }
                                                    }
                                                } else {
                                                    Spacer(modifier = Modifier.weight(1f))
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        if (selectedCalDay != null) {
                            Spacer(modifier = Modifier.height(16.dp))
                            val dayNum = selectedCalDay!!
                            val targetDateStr = "${dayNum.toString().padStart(2, '0')}/${calMonth.toString().padStart(2, '0')}/$calYear"
                            val dayRecs = historyRecords.filter { it.startDateString.startsWith(targetDateStr) }

                            Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp), colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))) {
                                Column(modifier = Modifier.padding(16.dp)) {
                                    Text("Fichajes del día $targetDateStr", fontSize = 14.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                    Spacer(modifier = Modifier.height(8.dp))
                                    if (dayRecs.isEmpty()) {
                                        Text("No hay registros para este día.", fontSize = 12.sp, color = Color(0xFF94A3B8))
                                    } else {
                                        dayRecs.forEach { r ->
                                            Text("• Entrada: ${r.startDateString} -> Salida: ${r.endDateString ?: "En curso"}", fontSize = 12.sp, color = Color.White)
                                            Text("  Horas netas: ${formatDurationMillis(r.netWorkedMillis)} - Pausas: ${formatDurationMillis(r.totalBreakMillis)}", fontSize = 11.sp, color = Color(0xFF38BDF8))
                                            Spacer(modifier = Modifier.height(6.dp))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                AppWorkerTab.PERFIL -> {
                    // PERFIL TAB
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(scrollState)
                            .padding(16.dp)
                    ) {
                        Text("👤 Perfil del Trabajador", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color.White, modifier = Modifier.fillMaxWidth())
                        Spacer(modifier = Modifier.height(16.dp))

                        Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp), colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))) {
                            Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                Text("Nombre: ${user.name}", fontSize = 14.sp, fontWeight = FontWeight.Bold, color = Color.White)
                                Text("Email: ${user.email}", fontSize = 12.sp, color = Color(0xFF94A3B8))
                                Text("Teléfono: ${user.phone}", fontSize = 12.sp, color = Color(0xFF94A3B8))
                                Text("Documento: ${user.docType.name} ${user.docNumber}", fontSize = 12.sp, color = Color(0xFF94A3B8))
                                Text("Puesto: ${user.position} • Depto: ${user.department}", fontSize = 12.sp, color = Color(0xFF38BDF8))
                                Text("Empresa: ${company?.name ?: "Sin asignar"}", fontSize = 12.sp, color = Color.White)
                            }
                        }

                        Spacer(modifier = Modifier.height(16.dp))

                        if (repository.getUsersByEmail(user.email).size > 1 && onSwitchCompany != null) {
                            StyledButtonWhite(text = "🏢 Cambiar de Empresa", onClick = onSwitchCompany)
                            Spacer(modifier = Modifier.height(10.dp))
                        }

                        Button(
                            onClick = {
                                if (user.isDemoUser) repository.clearDemoSessionData()
                                onLogout()
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                            shape = RoundedCornerShape(20.dp),
                            modifier = Modifier.fillMaxWidth().height(44.dp)
                        ) { Text("Cerrar sesión", fontWeight = FontWeight.Bold, fontSize = 14.sp) }
                    }
                }

                else -> {}
            }
        }

        // 🎨 BOTTOM NAVIGATION BAR (5 Items)
        Surface(
            color = Color(0xFF0F172A),
            shadowElevation = 12.dp,
            modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth()
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(64.dp)
                    .padding(horizontal = 4.dp),
                horizontalArrangement = Arrangement.SpaceEvenly,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Item 1: Inicio
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.weight(1f).clickable { selectedTab = AppWorkerTab.INICIO }
                ) {
                    Text("🏠", fontSize = 18.sp)
                    Text("Inicio", fontSize = 10.sp, color = if (selectedTab == AppWorkerTab.INICIO) Color(0xFF38BDF8) else Color(0xFF94A3B8), fontWeight = FontWeight.Bold)
                }

                // Item 2: Vacaciones
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.weight(1f).clickable { selectedTab = AppWorkerTab.VACACIONES }
                ) {
                    Text("☀️", fontSize = 18.sp)
                    Text("Vacaciones", fontSize = 10.sp, color = if (selectedTab == AppWorkerTab.VACACIONES) Color(0xFF38BDF8) else Color(0xFF94A3B8), fontWeight = FontWeight.Bold)
                }

                // Item 3: Central Action FAB Button
                val isWorking = activeClockIn?.currentStatus == ClockInStatus.WORKING || activeClockIn?.currentStatus == ClockInStatus.ON_BREAK
                Surface(
                    onClick = {
                        val currentStatus = activeClockIn?.currentStatus ?: ClockInStatus.NOT_STARTED
                        if (currentStatus == ClockInStatus.NOT_STARTED) {
                            showStartShiftConfirmDialog = true
                        } else {
                            showStopShiftOptionDialog = true
                        }
                    },
                    color = if (isWorking) Color(0xFFDC2626) else Color(0xFF16A34A),
                    shape = CircleShape,
                    modifier = Modifier.size(48.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(if (isWorking) "■" else "▶", fontSize = 20.sp, color = Color.White)
                    }
                }

                // Item 4: Calendario
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.weight(1f).clickable { selectedTab = AppWorkerTab.CALENDARIO }
                ) {
                    Text("📅", fontSize = 18.sp)
                    Text("Calendario", fontSize = 10.sp, color = if (selectedTab == AppWorkerTab.CALENDARIO) Color(0xFF38BDF8) else Color(0xFF94A3B8), fontWeight = FontWeight.Bold)
                }

                // Item 5: Perfil
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.weight(1f).clickable { selectedTab = AppWorkerTab.PERFIL }
                ) {
                    Text("👤", fontSize = 18.sp)
                    Text("Perfil", fontSize = 10.sp, color = if (selectedTab == AppWorkerTab.PERFIL) Color(0xFF38BDF8) else Color(0xFF94A3B8), fontWeight = FontWeight.Bold)
                }
            }
        }
    }

    // Modal: Confirm Start Shift
    if (showStartShiftConfirmDialog) {
        AlertDialog(
            onDismissRequest = { showStartShiftConfirmDialog = false },
            containerColor = Color(0xFF1E293B),
            title = {
                Text("▶ Confirmar Inicio de Jornada", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color.White)
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        text = "¿Deseas adjuntar tus datos de ubicación GPS con este fichaje de inicio de jornada?",
                        fontSize = 13.sp,
                        color = Color(0xFF94A3B8)
                    )
                    Text(
                        text = "Hora de inicio: " + formatSimpleTime(currentTimeMillis),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color.White
                    )
                }
            },
            confirmButton = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Button(
                        onClick = {
                            val fetchGps = onRequestGpsLocation ?: { onResult -> getCurrentGpsLocation(onResult) }
                            fetchGps { deviceLoc ->
                                repository.startClockIn(
                                    user = user,
                                    isLocationVerified = deviceLoc != null,
                                    location = deviceLoc?.formattedAddress ?: if (isLocationSharingEnabled) "Ubicación Verificada" else null,
                                    latitude = deviceLoc?.latitude,
                                    longitude = deviceLoc?.longitude,
                                    altitude = deviceLoc?.altitude
                                )
                                confirmationBannerMessage = "✅ ¡Jornada laboral iniciada con éxito a las " + formatSimpleTime(currentTimeMillis) + "!"
                                showStartShiftConfirmDialog = false
                                refreshState()
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(42.dp)
                    ) {
                        Text("📍 Iniciar con Ubicación GPS", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    }

                    Button(
                        onClick = {
                            repository.startClockIn(
                                user = user,
                                isLocationVerified = false,
                                location = null
                            )
                            confirmationBannerMessage = "✅ ¡Jornada iniciada sin ubicación a las " + formatSimpleTime(currentTimeMillis) + "!"
                            showStartShiftConfirmDialog = false
                            refreshState()
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(42.dp)
                    ) {
                        Text("▶ Iniciar Sin Ubicación Esta Vez", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    }

                    StyledButtonWhite(
                        text = "Cancelar",
                        onClick = { showStartShiftConfirmDialog = false }
                    )
                }
            },
            dismissButton = null
        )
    }

    // Modal: Stop Shift Option Dialog (Pausa vs Finalizar)
    if (showStopShiftOptionDialog && activeClockIn != null) {
        val record = activeClockIn!!
        val isCurrentlyWorking = record.currentStatus == ClockInStatus.WORKING

        AlertDialog(
            onDismissRequest = { showStopShiftOptionDialog = false },
            containerColor = Color(0xFF1E293B),
            title = {
                Text("Detener Jornada Laboral", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color.White)
            },
            text = {
                Text(
                    text = "¿Qué deseas hacer con tu jornada en curso?",
                    fontSize = 13.sp,
                    color = Color(0xFF94A3B8)
                )
            },
            confirmButton = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    if (isCurrentlyWorking) {
                        Button(
                            onClick = {
                                repository.startBreak(record.id)
                                confirmationBannerMessage = "⏸ En descanso"
                                showStopShiftOptionDialog = false
                                refreshState()
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFD97706)),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth().height(42.dp)
                        ) {
                            Text("⏸ Comenzar Descanso / Pausa", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                        }
                    } else {
                        Button(
                            onClick = {
                                repository.resumeFromBreak(record.id)
                                confirmationBannerMessage = "▶ Jornada reanudada"
                                showStopShiftOptionDialog = false
                                refreshState()
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0284C7)),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth().height(42.dp)
                        ) {
                            Text("▶ Reanudar Jornada", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                        }
                    }

                    Button(
                        onClick = {
                            val fetchGps = onRequestGpsLocation ?: { onResult -> getCurrentGpsLocation(onResult) }
                            fetchGps { deviceLoc ->
                                repository.endClockIn(
                                    clockInId = record.id,
                                    endLocation = deviceLoc?.formattedAddress ?: if (isLocationSharingEnabled) "Ubicación Verificada" else null,
                                    latitude = deviceLoc?.latitude,
                                    longitude = deviceLoc?.longitude,
                                    altitude = deviceLoc?.altitude
                                )
                                confirmationBannerMessage = "🔴 Jornada laboral finalizada con éxito."
                                showStopShiftOptionDialog = false
                                refreshState()
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(42.dp)
                    ) {
                        Text("🔴 Finalizar con Ubicación GPS", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    }

                    Button(
                        onClick = {
                            repository.endClockIn(
                                clockInId = record.id,
                                endLocation = null
                            )
                            confirmationBannerMessage = "🔴 Jornada laboral finalizada sin ubicación."
                            showStopShiftOptionDialog = false
                            refreshState()
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF334155)),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().height(42.dp)
                    ) {
                        Text("🔴 Finalizar Sin Ubicación Esta Vez", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    }

                    StyledButtonWhite(
                        text = "Cancelar",
                        onClick = { showStopShiftOptionDialog = false }
                    )
                }
            },
            dismissButton = null
        )
    }
}

private fun calculateWeekDays(currentMillis: Long, weekOffset: Int): List<DynamicDayInfo> {
    val dayMillis = 86_400_000L
    val daysSinceEpoch = (currentMillis / dayMillis).toInt()
    val dayOfWeekMonBased = (daysSinceEpoch + 3) % 7 // 0=Mon..6=Sun
    val mondayMillis = (daysSinceEpoch - dayOfWeekMonBased) * dayMillis + (weekOffset * 7 * dayMillis)
    val dayNames = listOf("LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM")

    return (0..6).map { i ->
        val dayTime = mondayMillis + (i * dayMillis) + 12 * 3600 * 1000L
        val dateCompact = formatCompactDateTimeString(dayTime)
        val year = dateCompact.substring(0, 4).toIntOrNull() ?: 2026
        val month = dateCompact.substring(4, 6).toIntOrNull() ?: 3
        val day = dateCompact.substring(6, 8).toIntOrNull() ?: 1
        val dd = day.toString().padStart(2, '0')
        val mm = month.toString().padStart(2, '0')
        val dateStr = "$dd/$mm/$year"

        val todayCompact = formatCompactDateTimeString(currentMillis).take(8)
        val isToday = dateCompact.take(8) == todayCompact

        DynamicDayInfo(
            dayName = dayNames[i],
            dayNumber = day,
            monthNumber = month,
            yearNumber = year,
            dateStr = dateStr,
            isToday = isToday
        )
    }
}

private fun getDaysInMonth(year: Int, month: Int): Int {
    return when (month) {
        2 -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
        4, 6, 9, 11 -> 30
        else -> 31
    }
}

private fun getDayOfWeekForFirstDayOfMonth(year: Int, month: Int): Int {
    var y = year
    var m = month
    if (m < 3) {
        m += 12
        y -= 1
    }
    val k = y % 100
    val j = y / 100
    val h = (1 + (13 * (m + 1)) / 5 + k + k / 4 + j / 4 + 5 * j) % 7
    return when (h) {
        0 -> 6
        1 -> 7
        2 -> 1
        3 -> 2
        4 -> 3
        5 -> 4
        6 -> 5
        else -> 1
    }
}
